<template>
  <div class="bm-resend" :data-testid="`resend-at-${position}`">
    <div v-if="state === 'preview' || state === 'sending'" class="bm-resend__preview">
      <div
        ref="preview"
        tabindex="-1"
        :data-testid="`resend-preview-${orderId}`"
      >
        <p class="bm-resend__lead">
          Re-send {{ orderLabel }} to the decorator? This replaces the order
          there with its current details. Nothing is charged.
        </p>
        <p v-if="outcome && outcome.lineItems.length" class="bm-resend__lines">
          {{ outcome.lineItems.length }}
          {{ outcome.lineItems.length === 1 ? 'item' : 'items' }}:
          <span
            v-for="(li, i) in outcome.lineItems"
            :key="i"
            class="bm-resend__line"
          >{{ [li.productCode, li.color].filter(Boolean).join(' ') || li.description }}</span>
        </p>
        <p
          v-if="outcome && outcome.designsMissing.length"
          class="bm-resend__warn"
          role="alert"
          :data-testid="`resend-designs-${orderId}`"
        >{{ sentences.design_missing }}</p>
      </div>
      <div class="bm-resend__actions">
        <button
          v-if="!(outcome && outcome.designsMissing.length)"
          type="button"
          class="bm-btn bm-btn--primary"
          :disabled="state === 'sending'"
          :data-testid="`resend-confirm-${orderId}`"
          @click="confirm"
        >{{ state === 'sending' ? 'Re-sending...' : 'Yes, re-send' }}</button>
        <button
          v-if="state === 'preview'"
          type="button"
          class="bm-btn"
          :data-testid="`resend-cancel-${orderId}`"
          @click="cancel"
        >Cancel</button>
      </div>
    </div>

    <div v-else class="bm-resend__actions">
      <button
        type="button"
        class="bm-btn"
        ref="control"
        :disabled="busy"
        :data-testid="`resend-${orderId}`"
        @click="start"
      >{{ state === 'checking' ? 'Checking...' : 'Re-send order' }}</button>
      <button
        v-if="state === 'checking'"
        type="button"
        class="bm-btn"
        :data-testid="`resend-cancel-${orderId}`"
        @click="cancel"
      >Cancel</button>
    </div>

    <!--
      The live region is in the DOM from the start and only its text swaps:
      many screen readers do not announce a region inserted with its text.
    -->
    <p
      class="bm-resend__message"
      role="status"
      :data-testid="`resend-message-${orderId}`"
    >{{ message }}</p>
  </div>
</template>

<script>
/**
 * Re-send ONE already-submitted order from the preflight list (card #643).
 *
 * Owns the whole request flow so the list stays a list:
 *
 *   idle -> checking (dry run, nothing sent) -> preview (what would be sent)
 *        -> sending (`{confirm: true}`) -> idle + `resent` event, or idle +
 *        a plain sentence for the refusal.
 *
 * The server decides everything; this layer words its answer through
 * `resendMessages` (one token -> sentence map) and never shows a raw token.
 *
 * DOUBLE-CLICK SAFE two ways: the control is disabled while a request is in
 * flight, and every request takes a ticket (`cancel` takes one too) so only
 * the LATEST may write. A dry run that answers after Cancel must not reopen
 * the preview, and a late answer must not overwrite a newer one.
 */
import { resendOrder } from "@/services/batchManagementService";
import {
  RESEND_SENTENCES,
  RESEND_UNCERTAIN_SENTENCE,
  resendSentence,
  resendStatusSentence,
} from "./resendMessages";

export default {
  name: "ResendOrder",
  props: {
    orderId: { type: [String, Number], required: true },
    orderLabel: { type: String, default: "this order" },
    // Position of the order in the list: the ui-runner cannot know a seeded
    // order's id when a scenario is authored.
    position: { type: Number, default: 0 },
  },
  emits: ["resent"],
  data() {
    return {
      state: "idle", // idle | checking | preview | sending
      outcome: null,
      message: "",
      ticket: 0,
    };
  },
  computed: {
    busy() {
      return this.state === "checking" || this.state === "sending";
    },
    sentences() {
      return RESEND_SENTENCES;
    },
  },
  methods: {
    focusLater(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el && typeof el.focus === "function") el.focus();
      });
    },
    sentenceFor(err) {
      const status = err && err.response && err.response.status;
      return resendStatusSentence(status);
    },
    async start() {
      if (this.busy) return;
      const ticket = ++this.ticket;
      this.state = "checking";
      this.message = "";
      this.outcome = null;
      try {
        const out = await resendOrder(this.orderId);
        if (ticket !== this.ticket) return;
        if (out.result === "dry_run") {
          this.outcome = out;
          this.state = "preview";
          this.focusLater("preview");
        } else {
          this.message = resendSentence(out.result);
          this.state = "idle";
          this.focusLater("control");
        }
      } catch (err) {
        if (ticket !== this.ticket) return;
        this.message = this.sentenceFor(err);
        this.state = "idle";
        this.focusLater("control");
      }
    },
    async confirm() {
      if (this.state !== "preview") return;
      const ticket = ++this.ticket;
      this.state = "sending";
      this.message = "";
      try {
        const out = await resendOrder(this.orderId, { confirm: true });
        if (ticket !== this.ticket) return;
        this.state = "idle";
        this.outcome = null;
        if (out.sent && out.result === "resent") {
          this.$emit("resent", { orderId: this.orderId, orderLabel: this.orderLabel });
        } else {
          this.message = resendSentence(out.result);
          this.focusLater("control");
        }
      } catch (err) {
        if (ticket !== this.ticket) return;
        // No answer at all (network, timeout, a 5xx with no outcome body):
        // the server may have sent it, so never read as "nothing happened".
        const status = err && err.response && err.response.status;
        this.message = !status || status >= 500 || status === 408
          ? RESEND_UNCERTAIN_SENTENCE
          : this.sentenceFor(err);
        this.state = "idle";
        this.outcome = null;
        this.focusLater("control");
      }
    },
    cancel() {
      if (this.state === "sending") return;
      this.ticket += 1;
      this.state = "idle";
      this.outcome = null;
      this.message = "";
      this.focusLater("control");
    },
  },
};
</script>

<style scoped>
.bm-resend {
  margin-top: 8px;
}

.bm-resend__lead,
.bm-resend__lines,
.bm-resend__warn {
  margin: 0 0 6px;
}

.bm-resend__message {
  margin: 0;
}

.bm-resend__message:not(:empty) {
  margin-top: 6px;
}

.bm-resend__line + .bm-resend__line::before {
  content: ", ";
}

.bm-resend__message {
  color: var(--tf-text-muted);
}

.bm-resend__warn {
  font-weight: 600;
}

.bm-resend__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bm-btn {
  border-radius: var(--tf-radius-pill, 9999px);
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13.5px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--primary {
  background: var(--tf-primary);
  border-color: var(--tf-primary);
  color: var(--tf-fg-on-primary);
}

.bm-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
